import {
  DefaultError,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import { useInfiniteHandler } from 'hooks';
import { IInfinitePageResponse } from 'services/types';
import { apiClient } from 'utils';
import { getDefaultInfinitePageParam } from 'utils/service/defaultInfinitePageParam';
import { useResponseHandler } from 'utils/service/responseHandler';
import {
  IBackgroundServiceHealth,
  IDigitalDeliveryErrorsCount,
  IDigitalDeliveryErrorsDetails,
  IDigitalDeliveryErrorsDetailsQuery,
  IPendingWorkCount,
} from './types';

export const useGetBackgroundServiceHealth = () => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IBackgroundServiceHealth[], DefaultError>({
    queryKey: ['BackgroundServiceHealth'],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `BackgroundServiceHealth`,
      });

      return responseHandler(response, {
        groupName: `useGetBackgroundServiceHealth`,
        onNotFound: () => {},
      });
    },
  });
};

export const useGetPendingWorkCount = () => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IPendingWorkCount, DefaultError>({
    queryKey: ['BackgroundServiceHealth/PendingWorkCount'],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `BackgroundServiceHealth/PendingWorkCount`,
      });

      return responseHandler(response, {
        groupName: `useGetPendingWorkCount`,
        onNotFound: () => {},
      });
    },
  });
};

export const useGetDigitalDeliveryErrorsCount = () => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IDigitalDeliveryErrorsCount, DefaultError>({
    queryKey: ['BackgroundServiceHealth/DigitalDeliveryErrorsCount'],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `BackgroundServiceHealth/DigitalDeliveryErrorsCount`,
      });

      return responseHandler(response, {
        groupName: `useGetDigitalDeliveryErrorsCount`,
        onNotFound: () => {},
      });
    },
  });
};

export const useGetDigitalDeliveryErrorsDetails = (
  params: IDigitalDeliveryErrorsDetailsQuery,
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params.page,
    params.itemCount,
  );

  return useInfiniteQuery<IDigitalDeliveryErrorsDetails[], DefaultError>({
    queryKey: ['BackgroundServiceHealth/DigitalDeliveryErrorsDetails', params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'BackgroundServiceHealth/DigitalDeliveryErrorsDetails',
        params: {
          ...JSON.parse(JSON.stringify(params)),
          itemCount,
          page: pageParam,
        },
      });

      const data = await responseHandler<
        IInfinitePageResponse<IDigitalDeliveryErrorsDetails>
      >(response, {
        groupName: `useGetDigitalDeliveryErrorsDetails - ${pageParam}`,
      });

      return infiniteHandler<IDigitalDeliveryErrorsDetails>(data);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};
